import { useEffect, useMemo, useState } from 'react';
import { CheckCircle2, Circle, MinusCircle } from 'lucide-react';
import { Alert } from '../../../shared/components/Alert';
import { AutocompleteSelect } from '../../../shared/components/AutocompleteSelect';
import { Button } from '../../../shared/components/Button';
import { EmptyState } from '../../../shared/components/EmptyState';
import { Textarea } from '../../../shared/components/Textarea';
import { TextInput } from '../../../shared/components/TextInput';
import { useToast } from '../../../shared/components/Toast';
import { usePermissions } from '../../../shared/hooks/usePermissions';
import { FEATURES } from '../../../shared/lib/permissions';
import { Collaborator, corpoDoErro, mensagemDeErro } from '../api';
import { ReinstatementItem, Termination, TerminationTask } from '../api/desligamento';
import { useDocumentos } from '../hooks/cadastro';
import {
  useAgirNaTarefa, useCancelarDesligamento, useCancelarReintegracao, useConcluirDesligamento, useDesligamentos,
  useIniciarDesligamento, useModelosDesligamento, useMotivosReintegracao, useReenviarReintegracao, useReintegracoes,
  useSolicitarReintegracao,
} from '../hooks/desligamento';
import {
  NOTICE_OPTIONS, REINSTATEMENT_STATUS_LABELS, REINSTATEMENT_STEP_LABELS, TERMINATION_STATUS_LABELS, formatDate, requiredProgress,
  terminationTypesFor,
} from './format';
import { PessoasModal } from './PessoasModal';

const hoje = () => new Intl.DateTimeFormat('en-CA', { timeZone: 'America/Sao_Paulo' }).format(new Date());

/** Aba Desligamento (US-GP-009/010): processo com tarefas, conclusão e reintegração. */
export function DesligamentoTab({ person }: { person: Collaborator }) {
  const toast = useToast();
  const { can } = usePermissions();
  const open = person.bonds.find((b) => !b.closed);
  const lastClosed = person.bonds.find((b) => b.status === 'desligado' || b.status === 'suspenso');
  const desligamentos = useDesligamentos(person.id, can(FEATURES.pessoasDesligamentos, 'view'));
  const reintegracoes = useReintegracoes(person.id, can(FEATURES.pessoasReintegracoes, 'view'));
  const concluir = useConcluirDesligamento();
  const [starting, setStarting] = useState(false);
  const [cancelling, setCancelling] = useState<Termination | null>(null);
  const [task, setTask] = useState<{ task: TerminationTask; action: 'concluir' | 'dispensar' } | null>(null);
  const [requesting, setRequesting] = useState(false);
  const [resubmitting, setResubmitting] = useState<ReinstatementItem | null>(null);
  const [cancelReinst, setCancelReinst] = useState<ReinstatementItem | null>(null);
  const [pendencias, setPendencias] = useState<string[] | null>(null);

  const emAndamento = (desligamentos.data ?? []).find((t) => t.status === 'em_andamento');
  const podeIniciar = can(FEATURES.pessoasDesligamentos, 'create') && open && !emAndamento
    && ['ativo', 'em_experiencia', 'em_treinamento', 'suspenso', 'licenca_nao_remunerada', 'ferias'].includes(open.status);
  const reintegracaoAberta = (reintegracoes.data ?? []).some((r) => r.status === 'aguardando_aprovacao' || r.status === 'complementacao_solicitada');
  const podeReintegrar = can(FEATURES.pessoasReintegracoes, 'create') && !!lastClosed && !open && !reintegracaoAberta;

  async function concluirAgora(t: Termination) {
    setPendencias(null);
    try {
      await concluir.mutateAsync(t.id);
      toast.success('Desligamento concluído. O vínculo está encerrado.');
    } catch (err) {
      const corpo = corpoDoErro(err);
      if (corpo.pendencias) setPendencias(corpo.pendencias.map((p) => p.message));
      toast.error(mensagemDeErro(err, 'Não foi possível concluir o desligamento.'));
    }
  }

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap justify-end gap-2">
        {podeIniciar && <Button size="sm" variant="danger" onClick={() => setStarting(true)}>Iniciar desligamento</Button>}
        {podeReintegrar && <Button size="sm" onClick={() => setRequesting(true)}>Solicitar reintegração</Button>}
      </div>

      {pendencias && (
        <Alert variant="error" title="Tarefas obrigatórias pendentes">
          <ul className="list-disc pl-5">{pendencias.map((p) => <li key={p}>{p}</li>)}</ul>
        </Alert>
      )}

      {(desligamentos.data ?? []).length === 0 && (reintegracoes.data ?? []).length === 0 && (
        <EmptyState size="compact" title="Nenhum desligamento ou reintegração" />
      )}

      {(desligamentos.data ?? []).map((t) => {
        const [feitas, total] = requiredProgress(t.tasks);
        const ativo = t.status === 'em_andamento';
        return (
          <section key={t.id} className="space-y-3 rounded-lg border border-gray-200 p-4">
            <header className="flex flex-wrap items-start justify-between gap-2">
              <div>
                <p className="font-semibold text-gray-900">{t.typeLabel} · {TERMINATION_STATUS_LABELS[t.status]}</p>
                <p className="text-sm text-gray-700">
                  Desligamento em {formatDate(t.terminationDate)} · último dia {formatDate(t.lastWorkingDay)} · aviso {NOTICE_OPTIONS.find((n) => n.value === t.noticeKind)?.label}
                </p>
                <p className="text-xs text-gray-500">{t.reason}{t.cancelledReason ? ` · Cancelado: ${t.cancelledReason}` : ''}</p>
              </div>
              {ativo && can(FEATURES.pessoasDesligamentos, 'edit') && (
                <div className="flex gap-2">
                  <Button size="sm" loading={concluir.isPending} disabled={t.terminationDate > hoje()} onClick={() => concluirAgora(t)}>
                    Concluir ({feitas}/{total})
                  </Button>
                  <Button size="sm" variant="ghost" onClick={() => setCancelling(t)}>Cancelar</Button>
                </div>
              )}
            </header>
            <ul className="divide-y divide-gray-100 text-sm">
              {t.tasks.map((k) => (
                <li key={k.id} className="flex flex-wrap items-center justify-between gap-2 py-2">
                  <span className="flex items-start gap-2">
                    {k.status === 'concluida' ? <CheckCircle2 className="mt-0.5 h-4 w-4 text-green-600" />
                      : k.status === 'dispensada' ? <MinusCircle className="mt-0.5 h-4 w-4 text-gray-400" />
                        : <Circle className="mt-0.5 h-4 w-4 text-amber-500" />}
                    <span>
                      <span className="font-medium">{k.area}:</span> {k.title}{!k.required && <span className="text-xs text-gray-500"> (opcional)</span>}
                      <span className="block text-xs text-gray-500">
                        {k.assigneeName ? `Responsável: ${k.assigneeName}` : k.systemKey ? 'Verificada pelo sistema' : 'Equipe de RH'}
                        {k.doneByName ? ` · ${k.status === 'concluida' ? 'concluída' : 'dispensada'} por ${k.doneByName}` : ''}
                        {k.notes ? ` · ${k.notes}` : ''}
                      </span>
                    </span>
                  </span>
                  {ativo && k.status === 'pendente' && (can(FEATURES.pessoasDesligamentos, 'edit') || can(FEATURES.pessoasDesligamentoTarefas, 'edit')) && (
                    <span className="flex gap-2">
                      <Button size="sm" variant="secondary" onClick={() => setTask({ task: k, action: 'concluir' })}>Concluir</Button>
                      <Button size="sm" variant="ghost" onClick={() => setTask({ task: k, action: 'dispensar' })}>Dispensar</Button>
                    </span>
                  )}
                </li>
              ))}
            </ul>
          </section>
        );
      })}

      {(reintegracoes.data ?? []).map((r) => (
        <section key={r.id} className="space-y-2 rounded-lg border border-blue-200 p-4 text-sm">
          <p className="font-semibold text-gray-900">
            Reintegração · {r.reason.name} · {REINSTATEMENT_STATUS_LABELS[r.status]}
            {r.currentStep && <span className="font-normal text-gray-600"> (etapa: {REINSTATEMENT_STEP_LABELS[r.currentStep]})</span>}
          </p>
          <p className="text-gray-700">A partir de {formatDate(r.reinstatementDate)} · {r.justification}</p>
          {r.approvals.length > 0 && (
            <ul className="text-xs text-gray-600">
              {r.approvals.map((a, i) => (
                <li key={i}>{REINSTATEMENT_STEP_LABELS[a.step]}: {a.decision} por {a.decidedByName ?? '—'}{a.comment ? ` — ${a.comment}` : ''}</li>
              ))}
            </ul>
          )}
          {can(FEATURES.pessoasReintegracoes, 'edit') && (r.status === 'complementacao_solicitada' || r.status === 'aguardando_aprovacao') && (
            <div className="flex gap-2">
              {r.status === 'complementacao_solicitada' && <Button size="sm" onClick={() => setResubmitting(r)}>Complementar e reenviar</Button>}
              <Button size="sm" variant="ghost" onClick={() => setCancelReinst(r)}>Cancelar</Button>
            </div>
          )}
        </section>
      ))}

      {open && <IniciarModal open={starting} bondId={open.id} employmentType={open.employmentType} onClose={() => setStarting(false)}
        onDone={(m) => { setStarting(false); toast.success(m); }} />}
      {lastClosed && <ReintegrarModal open={requesting} bondId={lastClosed.id} personId={person.id} onClose={() => setRequesting(false)}
        onDone={(m) => { setRequesting(false); toast.success(m); }} />}
      <TaskModal state={task} onClose={() => setTask(null)} onDone={(m) => { setTask(null); toast.success(m); }} />
      <TextoModal
        open={cancelling !== null}
        title="Cancelar desligamento?"
        label="Justificativa"
        danger
        onClose={() => setCancelling(null)}
        useAction={useCancelarDesligamento}
        run={(fn, texto) => fn({ id: cancelling!.id, justification: texto })}
        done={(m) => { setCancelling(null); toast.success(m); }}
        okMessage="Desligamento cancelado; o vínculo voltou à situação anterior."
      />
      <TextoModal
        open={cancelReinst !== null}
        title="Cancelar reintegração?"
        label="Justificativa"
        danger
        onClose={() => setCancelReinst(null)}
        useAction={useCancelarReintegracao}
        run={(fn, texto) => fn({ id: cancelReinst!.id, justification: texto })}
        done={(m) => { setCancelReinst(null); toast.success(m); }}
        okMessage="Reintegração cancelada."
      />
      <TextoModal
        open={resubmitting !== null}
        title="Complementar reintegração"
        label="Justificativa atualizada"
        initial={resubmitting?.justification}
        onClose={() => setResubmitting(null)}
        useAction={useReenviarReintegracao}
        run={(fn, texto) => fn({ id: resubmitting!.id, justification: texto })}
        done={(m) => { setResubmitting(null); toast.success(m); }}
        okMessage="Reintegração reenviada para aprovação."
      />
    </div>
  );
}

function IniciarModal({ open, bondId, employmentType, onClose, onDone }: {
  open: boolean; bondId: string; employmentType: string; onClose: () => void; onDone: (m: string) => void;
}) {
  const modelos = useModelosDesligamento(false, open);
  const iniciar = useIniciarDesligamento(bondId);
  const pj = employmentType === 'pj';
  const initial = () => ({
    templateId: '', type: pj ? 'encerramento_contrato_pj' : 'sem_justa_causa', reason: '', terminationDate: hoje(),
    lastWorkingDay: hoje(), noticeKind: pj ? 'nao_aplicavel' : 'indenizado', noticeDate: '', notes: '',
  });
  const [form, setForm] = useState(initial);
  const [error, setError] = useState<string | null>(null);
  const opcoes = useMemo(() => (modelos.data ?? []).filter((m) => m.employmentType === employmentType), [modelos.data, employmentType]);

  useEffect(() => {
    if (!open) return;
    setForm(initial());
    setError(null);
  }, [open]); // eslint-disable-line react-hooks/exhaustive-deps

  const set = (k: keyof ReturnType<typeof initial>, v: string) => setForm((f) => ({ ...f, [k]: v }));

  async function submit() {
    if (!form.templateId) return setError('Selecione o modelo.');
    if (!form.reason.trim()) return setError('Informe o motivo.');
    if (form.lastWorkingDay > form.terminationDate) return setError('O último dia trabalhado não pode ser depois do desligamento.');
    if (form.noticeKind === 'trabalhado' && !form.noticeDate) return setError('Informe a data do aviso.');
    try {
      await iniciar.mutateAsync({
        ...form, reason: form.reason.trim(), noticeDate: form.noticeDate || undefined, notes: form.notes.trim() || undefined,
      });
      onDone('Desligamento iniciado. As tarefas foram distribuídas às áreas.');
    } catch (err) {
      setError(mensagemDeErro(err, 'Não foi possível iniciar o desligamento.'));
    }
  }

  return (
    <PessoasModal open={open} size="lg" title="Iniciar desligamento" submitLabel="Iniciar" submitVariant="danger"
      isSubmitting={iniciar.isPending} submitError={error} onClose={onClose} onSubmit={submit}>
      <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
        <AutocompleteSelect label="Modelo" options={opcoes.map((m) => ({ value: m.id, label: m.name }))} value={form.templateId}
          onChange={(v) => set('templateId', v)} />
        <AutocompleteSelect label="Tipo" options={terminationTypesFor(employmentType)} value={form.type} onChange={(v) => set('type', v)} />
        <TextInput label="Data do desligamento" type="date" value={form.terminationDate} onChange={(e) => set('terminationDate', e.target.value)} />
        <TextInput label="Último dia trabalhado" type="date" value={form.lastWorkingDay} onChange={(e) => set('lastWorkingDay', e.target.value)} />
        {!pj && <AutocompleteSelect label="Aviso prévio" options={NOTICE_OPTIONS} value={form.noticeKind} onChange={(v) => set('noticeKind', v)} />}
        {form.noticeKind === 'trabalhado' && (
          <TextInput label="Data do aviso" type="date" value={form.noticeDate} onChange={(e) => set('noticeDate', e.target.value)} />
        )}
      </div>
      <Textarea label="Motivo" value={form.reason} onChange={(e) => set('reason', e.target.value)} />
      <Textarea label="Observações (opcional)" value={form.notes} onChange={(e) => set('notes', e.target.value)} />
    </PessoasModal>
  );
}

function ReintegrarModal({ open, bondId, personId, onClose, onDone }: {
  open: boolean; bondId: string; personId: string; onClose: () => void; onDone: (m: string) => void;
}) {
  const motivos = useMotivosReintegracao(false, open);
  const documentos = useDocumentos(personId, false, open);
  const solicitar = useSolicitarReintegracao(bondId);
  const [form, setForm] = useState({ reasonId: '', justification: '', reinstatementDate: hoje(), documentId: '' });
  const [error, setError] = useState<string | null>(null);
  const motivo = (motivos.data ?? []).find((m) => m.id === form.reasonId);

  useEffect(() => {
    if (!open) return;
    setForm({ reasonId: '', justification: '', reinstatementDate: hoje(), documentId: '' });
    setError(null);
  }, [open]);

  async function submit() {
    if (!form.reasonId) return setError('Selecione o motivo.');
    if (!form.justification.trim()) return setError('Justificativa é obrigatória.');
    if (motivo?.requiresDocument && !form.documentId) return setError('Este motivo exige documento comprobatório.');
    try {
      await solicitar.mutateAsync({
        reasonId: form.reasonId, justification: form.justification.trim(), reinstatementDate: form.reinstatementDate,
        documentId: form.documentId || undefined,
      });
      onDone('Reintegração enviada para aprovação.');
    } catch (err) {
      setError(mensagemDeErro(err, 'Não foi possível solicitar a reintegração.'));
    }
  }

  return (
    <PessoasModal open={open} size="lg" title="Solicitar reintegração"
      description="A reintegração reabre o mesmo vínculo depois das aprovações. Para recontratar, crie um vínculo novo."
      submitLabel="Solicitar" isSubmitting={solicitar.isPending} submitError={error} onClose={onClose} onSubmit={submit}>
      <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
        <AutocompleteSelect label="Motivo" options={(motivos.data ?? []).map((m) => ({ value: m.id, label: m.name }))} value={form.reasonId}
          onChange={(v) => setForm((f) => ({ ...f, reasonId: v }))} />
        <TextInput label="Data de reintegração" type="date" value={form.reinstatementDate}
          onChange={(e) => setForm((f) => ({ ...f, reinstatementDate: e.target.value }))} />
        <AutocompleteSelect
          label={motivo?.requiresDocument ? 'Documento comprobatório' : 'Documento (opcional)'}
          options={(documentos.data ?? []).map((d) => ({ value: d.id, label: `${d.typeName} · ${formatDate(d.uploadedAt)}` }))}
          value={form.documentId}
          onChange={(v) => setForm((f) => ({ ...f, documentId: v }))}
        />
      </div>
      {motivo?.requiresLegalApproval && <p className="text-xs text-gray-500">Este motivo passa também pela aprovação jurídica.</p>}
      <Textarea label="Justificativa" value={form.justification} onChange={(e) => setForm((f) => ({ ...f, justification: e.target.value }))} />
    </PessoasModal>
  );
}

function TaskModal({ state, onClose, onDone }: {
  state: { task: TerminationTask; action: 'concluir' | 'dispensar' } | null; onClose: () => void; onDone: (m: string) => void;
}) {
  const agir = useAgirNaTarefa();
  const [notes, setNotes] = useState('');
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    setNotes('');
    setError(null);
  }, [state]);

  async function submit() {
    if (!state) return;
    if (state.action === 'dispensar' && !notes.trim()) return setError('Dispensar exige justificativa.');
    try {
      await agir.mutateAsync({ id: state.task.id, action: state.action, notes: notes.trim() || undefined });
      onDone(state.action === 'concluir' ? 'Tarefa concluída.' : 'Tarefa dispensada.');
    } catch (err) {
      setError(mensagemDeErro(err, 'Não foi possível registrar a tarefa.'));
    }
  }

  return (
    <PessoasModal open={state !== null} title={state?.action === 'concluir' ? 'Concluir tarefa' : 'Dispensar tarefa'}
      description={state ? `${state.task.area}: ${state.task.title}` : undefined} submitLabel="Confirmar"
      isSubmitting={agir.isPending} submitError={error} onClose={onClose} onSubmit={submit}>
      <Textarea label={state?.action === 'dispensar' ? 'Justificativa' : 'Observação (opcional)'} value={notes}
        onChange={(e) => setNotes(e.target.value)} />
    </PessoasModal>
  );
}

/** Modal de um campo de texto que dispara uma mutation (cancelamentos e complementação). */
function TextoModal<TArgs>({ open, title, label, initial, danger, onClose, useAction, run, done, okMessage }: {
  open: boolean; title: string; label: string; initial?: string; danger?: boolean; onClose: () => void;
  useAction: () => { mutateAsync: (a: TArgs) => Promise<unknown>; isPending: boolean };
  run: (fn: (a: TArgs) => Promise<unknown>, texto: string) => Promise<unknown>;
  done: (m: string) => void; okMessage: string;
}) {
  const action = useAction();
  const [texto, setTexto] = useState('');
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!open) return;
    setTexto(initial ?? '');
    setError(null);
  }, [open, initial]);

  async function submit() {
    if (!texto.trim()) return setError('Preencha o campo.');
    try {
      await run(action.mutateAsync, texto.trim());
      done(okMessage);
    } catch (err) {
      setError(mensagemDeErro(err, 'Não foi possível concluir a operação.'));
    }
  }

  return (
    <PessoasModal open={open} title={title} submitLabel="Confirmar" submitVariant={danger ? 'danger' : 'primary'}
      isSubmitting={action.isPending} submitError={error} onClose={onClose} onSubmit={submit}>
      <Textarea label={label} value={texto} onChange={(e) => setTexto(e.target.value)} />
    </PessoasModal>
  );
}
